"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Loader2, Printer, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface QRCodeCardProps {
    listingId: string;
    title: string;
    onGenerate?: () => void;
}

export function QRCodeCard({ listingId, title }: QRCodeCardProps) {
    const [qrData, setQrData] = useState<{ image: string; code: string; scanCount: number } | null>(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const res = await fetch("/api/qr/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ listingId }),
            });

            if (!res.ok) throw new Error("Erreur génération");

            const data = await res.json();
            setQrData(data);
        } catch {
            toast.error("Impossible de générer le QR Code");
        } finally {
            setLoading(false);
        }
    };

    // Open/Generate on trigger
    const handleOpen = (isOpen: boolean) => {
        setOpen(isOpen);
        if (isOpen && !qrData) {
            generate();
        }
    };

    const handlePrint = () => {
        if (!qrData) return;

        const printWindow = window.open("", "_blank");
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
            <head>
                <title>QR Code - ${title}</title>
                <style>
                    body { font-family: sans-serif; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; }
                    .card { border: 2px solid #ddd; padding: 40px; border-radius: 20px; text-align: center; max-width: 400px; }
                    .logo { font-size: 24px; font-weight: bold; color: #D10074; margin-bottom: 20px; }
                    .title { font-size: 18px; color: #333; margin-bottom: 30px; }
                    .qr { width: 250px; height: 250px; margin-bottom: 20px; }
                    .footer { font-size: 12px; color: #666; margin-top: 20px; }
                    .code { font-family: monospace; font-size: 14px; background: #eee; padding: 5px 10px; border-radius: 5px; }
                </style>
            </head>
            <body>
                <div class="card">
                    <div class="logo">HandiPrêt 💝</div>
                    <div class="title">${title}</div>
                    <img src="${qrData.image}" class="qr" />
                    <div><span class="code">${qrData.code}</span></div>
                    <div class="footer">
                        Scannez ce code pour voir l'annonce complète.<br/><br/>
                        Matériel médical solidaire pour enfants<br/>
                        www.handipret.fr
                    </div>
                </div>
                <script>
                    window.onload = () => { window.print(); window.close(); }
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <Dialog open={open} onOpenChange={handleOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                    <QrCode className="size-4" />
                    QR Code Solidaire
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>QR Code de l&apos;annonce</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col items-center justify-center space-y-6 py-6">
                    {loading ? (
                        <Loader2 className="size-8 animate-spin text-muted-foreground" />
                    ) : qrData ? (
                        <>
                            <div className="relative border-4 border-hp-pink p-4 rounded-xl bg-white shadow-lg">
                                <Image
                                    src={qrData.image}
                                    alt="QR Code"
                                    width={200}
                                    height={200}
                                    className="rounded-lg"
                                />
                                <div className="absolute -bottom-3 inset-x-0 flex justify-center">
                                    <span className="bg-hp-pink text-white text-xs font-bold px-3 py-1 rounded-full">
                                        {qrData.code}
                                    </span>
                                </div>
                            </div>

                            <div className="text-center space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    Déjà scanné <strong className="text-hp-teal">{qrData.scanCount} fois</strong>
                                </p>
                                <p className="text-xs text-muted-foreground max-w-xs">
                                    Imprimez ce code et collez-le sur votre matériel ou dans un secrétariat médical.
                                </p>
                            </div>

                            <Button onClick={handlePrint} className="w-full gap-2 bg-hp-teal hover:bg-hp-teal-dark">
                                <Printer className="size-4" />
                                Imprimer l&apos;affichette
                            </Button>
                        </>
                    ) : (
                        <p className="text-destructive">Erreur de chargement</p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
